import React, { Suspense, useEffect } from 'react'
import Main from './Main'
import 'react-datepicker/dist/react-datepicker.css'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { AppWrapper } from './App.styled'
import AppModal from './component/GlobalAppModal'
import { getPhonesThunk, getVoipDesktopSessionsThunk } from './redux/thunk/phone'
import { getPrinterDesktopSessionsThunk } from './redux/thunk/printer'
import { setSystemKeyboardText, setSystemShowKeyboard, triggerSystemKeyboardEnter, setPage } from './redux/action/system'
import ErrorBoundary from './helper/ErrorBoundary'
import { GlobalStyle } from './style/global.styled'
import Keyboard from './component/Elements/keyboard'
import { connectSocketIoThunk } from './redux/thunk/socket'
import { selectCompanyIndex, selectIsAuthorized, selectUsername } from './redux/selector/system'
import { getSupportDataThunk } from './redux/thunk/support'
import { setSupportDealerContacts } from './redux/action/support'
import { useLocalStorage } from './hooks/useLocalStorage'
import { useSessionResume } from './hooks/useSessionResume'
import SocketClientMonitorCart from './component/ClientMonitorCart/SocketClientMonitorCart'
import { extractQueryParam, isClientMonitor, withJsClientMonitor, withSocketClientMonitor } from './helper/clientMonitor'
import ClientMonitorWindow from './component/ClientMonitorWindow'
import JsClientMonitorCart from './component/ClientMonitorCart/JsClientMonitorCart'
import { getConfigJsDataThunk } from './redux/thunk/configJs'
import { getCompanyRegistrationDataThunk } from './redux/thunk/company'
import { getTseConfigThunk, getTseNotificationThunk } from './redux/thunk/tse'
import { getAdyenConnectedAccountThunk, getPayPalNotificationThunk, getStripeNotificationThunk } from './redux/thunk/payment'
import { setTap2PayNotificationModalShow } from './redux/action/payment'
import { getViaPreOrdersTodayThunk } from './redux/thunk/order'
import { SIZE } from './component/GlobalAppModal/TodayPreOrderModal/constants'
import { getDateParameterString, setWorkingHoursHelper } from './helper'
import { setCompanyRegistrationData } from './redux/action/company'
import { updateClientMonitorConfigThunk } from './redux/thunk/clientMonitorConfig'
import NativeAppListener from './component/NativeAppListener'
import { appNotificationDeviceThunk } from './redux/thunk/registration/auth'
import { getCompanyDataThunk } from './redux/thunk/menu'
import { getPageThunk } from './redux/thunk/page'
import { fetchAutoConfirmationThunk } from './redux/thunk/order/acceptOrder'
import AppStatus from './component/AppStatus'
import LoadLocalStorage from './component/LoadLocalStorage'
import AppThemeProvider from './style/theme/provider'
import TimeCounter from './component/TimeCounter'
import { useTranslation } from 'react-i18next'

const App = React.memo(() => {
	const { i18n } = useTranslation()
	const language = i18n.language
	const isAuthorized = useSelector(selectIsAuthorized)
	const voipDesktopSessionsLength = useSelector(store => store.socket.voipDesktopSessions?.length)
	const keyboardText = useSelector(store => store.system.keyboardText)
	const showKeyboard = useSelector(store => store.system.showKeyboard)
	const dispatch = useDispatch()
	const companyIndex = useSelector(selectCompanyIndex)
	const username = useSelector(selectUsername)
	const isClientMontr = isClientMonitor()
	const location = useLocation()
	const [, setDesktopDeviceId] = useLocalStorage('desktopDeviceId', null)
	const [, setDesktopMode] = useLocalStorage('desktopMode', null)
	const [appMode, setAppMode] = useLocalStorage('appMode', null)
	const [, setAppNotificationData] = useLocalStorage('appNotificationData', null)

	const desktopDeviceId = extractQueryParam('desktopDeviceId')
	const desktopMode = extractQueryParam('desktopMode')
	const appModeParam = extractQueryParam('appMode')
	const socketClientMonitor = withSocketClientMonitor()
	const jsClientMonitor = withJsClientMonitor()
	const configClientMonitor = useSelector(store => store.clientMonitorConfig.config)
	const enabledClientMonitor = useSelector(store => store.clientMonitorConfig.config?.enabled)
	const appNotificationData = useSelector(store => store.notifications.appNotificationData)

	useEffect(() => {
		dispatch(getConfigJsDataThunk())
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (desktopDeviceId) {
			setDesktopDeviceId(desktopDeviceId)
		}
	}, [desktopDeviceId]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (desktopMode) {
			setDesktopMode(desktopMode)
		}
	}, [desktopMode]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (appModeParam) {
			setAppMode(appModeParam)
		}
	}, [appModeParam]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (appNotificationData) {
			setAppNotificationData(appNotificationData)
		}
	}, [appNotificationData]) // eslint-disable-line react-hooks/exhaustive-deps

	useSessionResume(isAuthorized && !isClientMontr)

	useEffect(() => {
		if (isAuthorized && !isClientMontr) {
			dispatch(connectSocketIoThunk())
			dispatch(getPrinterDesktopSessionsThunk())
			dispatch(getVoipDesktopSessionsThunk())
			dispatch(getPhonesThunk())
		}
	}, [isAuthorized, isClientMontr]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized) {
			dispatch(getPhonesThunk())
		}
	}, [voipDesktopSessionsLength]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized) {
			dispatch(getCompanyRegistrationDataThunk())
			dispatch(getSupportDataThunk())
		} else {
			dispatch(setCompanyRegistrationData(null))
			dispatch(setSupportDealerContacts(null))
		}
	}, [isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized) {
			dispatch(getTseNotificationThunk())
			dispatch(getPayPalNotificationThunk())
			dispatch(getStripeNotificationThunk())
			dispatch(getAdyenConnectedAccountThunk())
			dispatch(setTap2PayNotificationModalShow(true))
			const date = getDateParameterString(setWorkingHoursHelper(new Date()))
			dispatch(getViaPreOrdersTodayThunk(date, date, 'NOT_ACCEPTED,NOT_REGISTERED', null, null, 0, SIZE, true))
		}
	}, [isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized) {
			dispatch(appNotificationDeviceThunk(companyIndex, username))
		}
	}, [isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized && enabledClientMonitor ) {
			dispatch(updateClientMonitorConfigThunk({ ...configClientMonitor, language }))
		}
	}, [isAuthorized, enabledClientMonitor]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (companyIndex) {
			dispatch(getCompanyDataThunk(companyIndex))
		}
	}, [companyIndex]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized) {
			dispatch(getPageThunk())
			dispatch(fetchAutoConfirmationThunk())
		} else {
			dispatch(setPage([]))
		}
	}, [isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (isAuthorized) {
			dispatch(getPrinterDesktopSessionsThunk())
			dispatch(getVoipDesktopSessionsThunk())
			dispatch(getTseConfigThunk())
		}
	}, [location?.pathname, isAuthorized]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<GlobalStyle />
			<ErrorBoundary>
				<Suspense fallback={(<div>Loading..</div>)}>
					<AppThemeProvider>
						<AppWrapper id="app-wrapper">
							{isAuthorized && <AppStatus />}
							{isClientMontr && socketClientMonitor &&
								<SocketClientMonitorCart />}
							{isClientMontr && jsClientMonitor &&
								<JsClientMonitorCart />}
							{!isClientMontr &&
								<Main />}
							{showKeyboard &&
								<Keyboard onlyNumbers={true}
												currentValue={keyboardText}
												handleType={text => dispatch(setSystemKeyboardText(text))}
												close={() => dispatch(setSystemShowKeyboard(false))}
												enter={() => dispatch(triggerSystemKeyboardEnter())} />}
							{appMode && <NativeAppListener />}
						</AppWrapper>
						<AppModal />
						<ClientMonitorWindow />
						<LoadLocalStorage />
						<TimeCounter />
					</AppThemeProvider>
				</Suspense>
			</ErrorBoundary>
		</>
	)
})

App.displayName = 'App'
export default App
